import { Controls } from '@xyflow/react'
import styled from 'styled-components'
import SendIcon from '@mui/icons-material/Send'
import TextNode from '../customNodes/TextNode'
import EmailIcon from '@mui/icons-material/Email'
import PlaceIcon from '@mui/icons-material/PlaceOutlined'
import StartNode from '../customNodes/StartNode'
import ButtonsNode from '../customNodes/ButtonsNode'
import LayersIcon from '@mui/icons-material/LayersOutlined'
import Grid3x3Icon from '@mui/icons-material/Grid3x3'
import GamepadIcon from '@mui/icons-material/Gamepad'
import SubjectIcon from '@mui/icons-material/Subject'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import DevicesIcon from '@mui/icons-material/DevicesOutlined'
// import SettingsIcon from '@mui/icons-material/Settings'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import DonutSmallIcon from '@mui/icons-material/DonutSmall'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import ControlCameraIcon from '@mui/icons-material/ControlCamera'
// import SignalWifi3BarIcon from '@mui/icons-material/SignalWifi3Bar'
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayListOutlined'
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar'
import KeyboardCommandKeyIcon from '@mui/icons-material/KeyboardCommandKey'
import EmojiFlagsIcon from '@mui/icons-material/EmojiFlags'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import ForkRightIcon from '@mui/icons-material/ForkRight'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import SaveIcon from '@mui/icons-material/Save'
import TimerIcon from '@mui/icons-material/Timer'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import AppSettingsAltOutlinedIcon from '@mui/icons-material/AppSettingsAltOutlined'
// import SignalCellularAlt3BarOutlinedIcon from '@mui/icons-material/SignalCellularAltOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import ReportIcon from '@mui/icons-material/Report'

export type HeaderLink = {
  title: string
  url: string
  allowedFor: [string, string][] // If the user has *any* of these permissions, we show the link. TODO: Change this?
  dashboard?: boolean
}

export const HeaderLinks: HeaderLink[] = [
  {
    title: 'Frequency Request Form',
    url: '/frequency-request-form',
    allowedFor: [['write', 'requests']],
  },
  {
    title: 'Spectrum Manager Dashboard',
    url: '/spectrum-manager-dashboard',
    allowedFor: [
      ['read', 'requests'],
      ['read', 'locations'],
      ['read', 'devices'],
      ['read', 'platforms'],
      ['read', 'licenses'],
    ],
  },
  {
    title: 'Playbooks',
    url: '/playbooks',
    allowedFor: [['read', 'playbooks']],
  },
  {
    title: 'Map',
    url: '/map',
    allowedFor: [['read', 'map']],
  },
  {
    title: 'Users Management',
    url: '/users-management',
    allowedFor: [['write', 'users']],
  },
  {
    title: 'Dashboards',
    url: '/dashboards',
    allowedFor: [['read', 'playbooks']], // TODO: introduce a new dashboard permission for this, not playbooks
    dashboard: true,
  },
]

export const nodeIcon = (nodeType: { icon: string }) => {
  switch (nodeType.icon) {
    case 'Grid3x3':
      return Grid3x3Icon
    case 'Gamepad':
      return GamepadIcon
    case 'DonutSmall':
      return DonutSmallIcon
    case 'Email':
      return EmailIcon
    case 'EmojiFlags':
      return EmojiFlagsIcon
    case 'PrecisionManufacturing':
      return PrecisionManufacturingIcon
    case 'ForkRight':
      return ForkRightIcon
    case 'SaveAs':
      return SaveAsIcon
    case 'Save':
      return SaveIcon
    case 'Timer':
      return TimerIcon
    case 'PendingActions':
      return PendingActionsIcon
    case 'SignalCellular3Bar':
      return SignalCellular3BarIcon
    case 'Send':
      return SendIcon
    case 'LocationOn':
      return LocationOnIcon
    case 'Subject':
      return SubjectIcon
    default:
      return FeaturedPlayListIcon
  }
}

export const initialPagination = {
  count: 0,
  skip: 0,
  take: 10,
}

export const initialFilter = {
  column: '',
  values: [],
  logicalOperation: '',
  selectedValues: [],
  chosenValue: '',
}

export const ControlsStyled = styled(Controls)`
  button {
    background-color: ${(props) => props.theme.controlsBg};
    color: ${(props) => props.theme.controlsColor};
    border-bottom: 1px solid ${(props) => props.theme.controlsBorder};

    &:hover {
      background-color: ${(props) => props.theme.controlsBgHover};
    }

    path {
      fill: currentColor;
    }
  }
`

export const nodeTypeComponents = {
  map: TextNode,
  sfaf: TextNode,
  text: StartNode,
  email: TextNode,
  slider: TextNode,
  action: TextNode,
  buttons: ButtonsNode,
  requestId: TextNode,
  postRequest: TextNode,
  httpRequest: TextNode,
  geofence: TextNode,
  transform: TextNode,
  branch: TextNode,
  store: TextNode,
  lookup: TextNode,
  sleep: TextNode,
  timeseries: TextNode,

  mapExample: TextNode,
  sfafExample: TextNode,
  textExample: StartNode,
  emailExample: TextNode,
  sliderExample: TextNode,
  actionExample: TextNode,
  buttonsExample: ButtonsNode,
  requestIdExample: TextNode,
  postRequestExample: TextNode,

  default: TextNode,
}

export const RequestTableConfigs = {
  title: 'Requests',
  isViewable: true,
  isDeletable: false,
  isEditable: false,
  isSearchable: false,
  isSelectable: true,
  items: [
    { key: 'id_overrides', column: 'ID', isTag: false },
    { key: 'request_name', column: 'Request Name', isTag: false },
    { key: 'username', column: 'Username', isTag: true },
    {
      key: 'number_of_frequencies',
      width: 100,
      column: 'Quantity',
      editable: true,
      isTag: false,
    },
    { key: 'site_location', column: 'Site location', isTag: true },
    { key: 'equipment', column: 'Equipment', isTag: false },
    { key: 'unit', column: 'Unit', isTag: true },
    { key: 'radius_306', column: 'Radius 306', editable: true, isTag: true },
    { key: 'radius_406', column: 'Radius 406', editable: true, isTag: true },
    { key: 'created_at', column: 'Created at', isTag: false },
    { key: 'exercise_name', column: 'Exercise', isTag: false },
    { key: 'frequency_group_id', column: 'Frequency Group Id', isTag: false },
    { key: 'user_phone', column: 'User Phone', isTag: false },
    { key: 'classification', column: 'Classification', isTag: false },
    { key: 'lead_time', column: 'Lead time', isTag: false },
    { key: 'start_date', column: 'Start time', isDate: true, isTag: false },
    { key: 'end_date', column: 'End time', isDate: true, isTag: false },
    { key: 'coordinates', column: 'Coordinates', isTag: false },
    { key: 'frequency_start', column: 'Frequency start', isTag: false },
    { key: 'frequency_end', column: 'Frequency end', isTag: false },
    { key: 'status', column: 'Status', isTag: true },
    { key: 'frequency', column: 'Frequencies', isTag: false },
    { key: 'sub_unit', column: 'Sub unit', isTag: true },
    { key: 'receiving_unit', column: 'Receiving unit', isTag: true },
    { key: 'request_id', column: 'Request Id', isTag: false },
    { key: 'platform', column: 'Platform', isTag: false },
  ],
}

export const FrequencyPlansTableConfigs = {
  title: 'Open Frequency Requests',
  isViewable: false,
  isDeletable: false,
  isEditable: false,
  isSearchable: true,
  isSelectable: true,
  items: [
    { key: 'id', column: 'ID', isTag: false },
    { key: 'start_freq', column: 'Start Freq', isTag: false },
    { key: 'end_freq', column: 'End Freq', isTag: false },
    { key: 'f_114', column: 'F 114', isTag: false },
    { key: 'power', column: 'Power', isTag: false },
    { key: 'classification', column: 'Classification', isTag: false },
    { key: 'start_date', column: 'Start time', isDate: true, isTag: false },
    { key: 'end_date', column: 'End time', isDate: true, isTag: false },
    { key: 'site_location', column: 'Site location', isTag: true },
    { key: 'radius_306', column: 'Radius 306', isTag: true },
    { key: 'radius_406', column: 'Radius 406', isTag: true },
    { key: 'frequency', column: 'Frequencies', isTag: false },
    { key: 'unit', column: 'Unit', isTag: true },
    { key: 'sub_unit', column: 'Sub unit', isTag: true },
    { key: 'receiving_unit', column: 'Receiving unit', isTag: true },
    {
      key: 'request_name',
      column: 'Request Name',
      isTag: false,
    },
    { key: 'platform', column: 'Platform', isTag: false },
    { key: 'exercise_name', column: 'Exercise Name', isTag: false },
    { key: 'state', column: 'State', isTag: true },
    { key: 'lat_long', column: 'Lat Long', isTag: false },
  ],
}

export const lightModeTableColors = [
  {
    background: '#FFCACA',
    color: '#940304',
  },
  {
    background: '#A7F3D0',
    color: '#23734E',
  },
  {
    background: '#C8D2FF',
    color: '#2438A2',
  },
  {
    background: '#FDE68B',
    color: '#544301',
  },
  {
    background: '#FCD7BB',
    color: '#8D491B',
  },
  {
    background: '#BFDCFE',
    color: '#023E89',
  },
  {
    background: '#EECFF3',
    color: '#671D73',
  },
]

export const darkModeTableColors = [
  {
    background: '#FFCACA',
    color: '#940304',
  },
  {
    background: '#A7F3D0',
    color: '#23734E',
  },
  {
    background: '#C8D2FF',
    color: '#2438A2',
  },
  {
    background: '#FDE68B',
    color: '#544301',
  },
  {
    background: '#FCD7BB',
    color: '#8D491B',
  },
  {
    background: '#BFDCFE',
    color: '#023E89',
  },
  {
    background: '#EECFF3',
    color: '#671D73',
  },
]

export const LocationTemplateConfigs = {
  title: 'Location',
  isViewable: false,
  isDeletable: true,
  isEditable: true,
  items: [
    { key: 'id', column: 'ID', isTag: false },
    { key: 'template_name', column: 'Template Name', isTag: false },
    { key: 'code', column: 'Region Code(300)', isTag: true },
    { key: 'name', column: 'State Region Name', isTag: true },
    {
      key: 'transmitter_location',
      column: 'Transmitter Location(301)',
      isTag: false,
    },
    { key: 'longitude_deg', column: 'Longitude Deg', isTag: false },
    { key: 'longitude_min', column: 'Longitude Min', isTag: false },
    { key: 'longitude_sec', column: 'Longitude Sec', isTag: false },
    { key: 'latitude_deg', column: 'Latitude Deg', isTag: false },
    { key: 'latitude_min', column: 'Latitude Min', isTag: false },
    { key: 'latitude_sec', column: 'Latitude Sec', isTag: false },
    { key: 'afc', column: 'Afc', isTag: true },
    { key: 'afc_code', column: 'Afc code', isTag: false },
    { key: 'afc_coord_note', column: 'Afc coord note', isTag: false },
  ],
}

export const AddTemplateItems = [
  { key: 'template_name', column: 'Template Name', required: true },
  {
    key: 'transmitter_location',
    column: 'Transmitter Location',
    required: true,
  },
  { key: 'state_region_type_id', column: 'Region', required: true },
  { key: 'afc', column: 'Afc', required: false },
  { key: 'afc_code', column: 'Afc code', required: false },
  { key: 'afc_coord_note', column: 'Afc coord note', required: false },
  { key: 'longitude_deg', column: 'Longitude Deg', required: true },
  { key: 'longitude_min', column: 'Longitude Min', required: true },
  { key: 'longitude_sec', column: 'Longitude Sec', required: true },
  { key: 'latitude_deg', column: 'Latitude Deg', required: true },
  { key: 'latitude_min', column: 'Latitude Min', required: true },
  { key: 'latitude_sec', column: 'Latitude Sec', required: true },
]

export const ExercisesConfigs = {
  title: 'Exercises',
  isViewable: false,
  isDeletable: true,
  isEditable: false,
  items: [
    { key: 'id', column: 'ID', width: 250, isTag: false },
    { key: 'name', column: 'Name', width: 250, isTag: false },
    {
      key: 'start_date',
      column: 'Start Time',
      width: 250,
      isDate: true,
      isTag: false,
    },
    {
      key: 'end_date',
      column: 'End Time',
      width: 250,
      isDate: true,
      isTag: false,
    },
    { key: 'f_702', column: 'F 702', width: 250, isTag: false },
    { key: 'f_910', column: 'F 910', width: 250, isTag: false },
  ],
}

export const DevicesConfigs = {
  title: 'Devices',
  isViewable: true,
  isDeletable: true,
  isEditable: false,
  items: [
    { key: 'id', column: 'ID', isTag: false, width: 70 },
    { key: 'model', column: 'Model', isTag: false },
    { key: 'certification', column: 'Certification', isTag: false },
    { key: 'irac', column: 'Irac', isTag: false },
    { key: 'sps', column: 'SPS', isTag: false },
    { key: 'code', column: 'Code', isTag: false },
    { key: 'gcu', column: 'GCU', isTag: false },
    { key: 'last_updated', column: 'Last Updated', isDate: true, isTag: false },
  ],
}

export const PlatformsConfigs = {
  title: 'Platforms',
  isViewable: true,
  isDeletable: true,
  isEditable: false,
  items: [
    { key: 'id', column: 'ID', isTag: false, width: 70 },
    { key: 'platform', column: 'Platform', isTag: false, width: 300 },
    { key: 'name', column: 'Name', isTag: false, width: 300 },
    { key: 'max_altitude', column: 'Max Altitude', isTag: false, width: 300 },
    { key: 'station', column: 'Station', isTag: true, width: 300 },
  ],
}

export const DeviceTransmitterConfigs = {
  title: 'Device Transmitters',
  isViewable: false,
  isDeletable: false,
  isEditable: false,
  items: [
    {
      key: 'emission_designator',
      column: 'Emission Designator',
      isTag: false,
      isEditable: true,
    },
    { key: 'power', column: 'Power', isTag: false, isEditable: true },
    {
      key: 'tuning_step',
      column: 'Tuning Step',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'tuning_range_mhz_min',
      column: 'Tuning Range MHZ Min',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'tuning_range_mhz_max',
      column: 'Tuning Range MHZ Max',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'radar_tunability',
      column: 'Radar Tunability',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'sidelobe_suppression',
      column: 'Sidelob Suppression',
      isTag: false,
      isEditable: true,
    },
  ],
  subTitle: 'Pulses',
  subItems: [
    {
      key: 'pulse_duration_min',
      column: 'Pulse Duration Min',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'pulse_duration_max',
      column: 'Pulse Duration Max',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'pulse_repetition_rate_min',
      column: 'Pulse Repetition Rate Min',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'pulse_repetition_rate_max',
      column: 'Pulse Repetition Rate Max',
      isTag: false,
      isEditable: true,
    },
  ],
}

export const DeviceAntennaConfigs = {
  title: 'Device Antennas',
  isViewable: false,
  isDeletable: false,
  isEditable: false,
  items: [
    {
      key: 'nomenclature',
      column: 'Nomenclature',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'antenna_type',
      column: 'Antenna Type',
      isTag: true,
    },
    { key: 'serial', column: 'Serial', isTag: false },
  ],
  subTitle: 'TX Modes',
  subItems: [
    { key: 'mode', column: 'Mode', isTag: false, isEditable: true },
    {
      key: 'horizontal_scan_type',
      column: 'Horizontal Scan Type',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'vertical_scan_type',
      column: 'Vertical Scan Type',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'horizontal_beamwidth',
      column: 'Horizontal beamwith',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'vertical_beamwidth',
      column: 'Vertical beamwith',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'freq_min',
      column: 'Freq Min',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'freq_max',
      column: 'Freq Max',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'polarization_type',
      column: 'Polarization Type',
      isTag: false,
      isEditable: true,
    },
  ],
}

export const OpenFrequnecyRequestsConfigs = {
  title: 'Device Antennas',
  isViewable: false,
  isDeletable: false,
  isEditable: false,
  items: [
    {
      key: 'nomenclature',
      column: 'Nomenclature',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'antenna_type',
      column: 'Antenna Type',
      isTag: true,
    },
    { key: 'serial', column: 'Serial', isTag: false },
  ],
  subTitle: 'TX Modes',
  subItems: [
    { key: 'mode', column: 'Mode', isTag: false, isEditable: true },
    {
      key: 'horizontal_scan_type',
      column: 'Horizontal Scan Type',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'vertical_scan_type',
      column: 'Vertical Scan Type',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'horizontal_beamwidth',
      column: 'Horizontal beamwith',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'vertical_beamwidth',
      column: 'Vertical beamwith',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'freq_min',
      column: 'Freq Min',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'freq_max',
      column: 'Freq Max',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'polarization_type',
      column: 'Polarization Type',
      isTag: false,
      isEditable: true,
    },
  ],
}

export const FrequencyPoolAssignmentsTableConfig = {
  title: 'Frequency Pool Assignments',
  isViewable: true,
  isDeletable: false,
  isEditable: false,
  isSearchable: true,
  isNominating: true,
  items: [
    {
      key: 'id',
      column: 'ID',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_102',
      column: 'Field 102',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_107',
      column: 'Field 107',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_110_from',
      column: 'Frequency 110',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_110_to',
      column: 'Field 110 To',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_110_reference',
      column: 'Reference',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_130',
      column: 'Field 130',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_142',
      column: 'Field 142',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_143',
      column: 'Field 143',
      isTag: false,
      isEditable: true,
    },
    {
      key: 'field_144',
      column: 'Field 144',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_151',
      column: 'Field 151',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_200',
      column: 'Field 200',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_204',
      column: 'Field 204',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_205',
      column: 'Field 205',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_206',
      column: 'Field 206',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_207',
      column: 'Field 207',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_209',
      column: 'Field 209',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_300',
      column: 'Field 300',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_301',
      column: 'Field 301',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_341',
      column: 'Field 341',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_373',
      column: 'Field 373',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_400',
      column: 'Field 400',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_401',
      column: 'Field 401',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_402',
      column: 'Field 402',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_511',
      column: 'Field 511',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_512',
      column: 'Field 512',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_520',
      column: 'Field 520',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_530',
      column: 'Field 530',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_701',
      column: 'Field 701',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_702',
      column: 'Field 702',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_803',
      column: 'Field 803',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_911',
      column: 'Field 911',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_924',
      column: 'Field 924',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_927',
      column: 'Field 927',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_928',
      column: 'Field 928',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_956',
      column: 'Field 956',
      isTag: false,
      isEditable: false,
    },
    {
      key: 'field_958',
      column: 'Field 958',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_005',
      column: 'Field 005',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'field_010',
      column: 'Field 010',
      isTag: true,
      isEditable: false,
    },
    {
      key: 'raw_sfaf',
      column: 'Raw SFAF',
      isTag: false,
      isEditable: true,
    },
  ],
}

export const ManagerDashboardTabs = [
  {
    label: 'Requests',
    icon: SyncAltIcon,
    link: 'requests',
  },
  {
    label: 'Links Diagram',
    icon: KeyboardCommandKeyIcon,
    link: 'links-diagram',
  },
  {
    label: 'Field Controls',
    icon: ControlCameraIcon,
    isMenuCollapsed: false,
    subItems: [
      {
        label: 'Exercises',
        link: 'exercises',
        icon: FeaturedPlayListIcon,
      },
      {
        label: 'Locations',
        link: 'locations',
        icon: PlaceIcon,
      },
    ],
  },
  {
    label: 'Devices & Platforms',
    icon: AppSettingsAltOutlinedIcon,
    isMenuCollapsed: false,
    subItems: [
      {
        label: 'Devices',
        link: 'devices',
        icon: DevicesIcon,
      },
      {
        label: 'Platforms',
        link: 'platforms',
        icon: LayersIcon,
      },
    ],
  },
  {
    label: 'Licenses',
    link: 'licenses',
    icon: ReceiptLongIcon,
  },
]

export const RequestsFormTabs = [
  {
    label: 'General',
  },
  {
    label: 'Location',
  },
  {
    label: 'Frequency',
  },
  {
    label: 'Equipment',
  },
]

export const dashboardIcons = {
  Check: CheckIcon,
  CheckCircle: CheckCircleIcon,
  Warning: WarningIcon,
  Error: ErrorIcon,
  Block: ReportIcon,
}
